// ─── Risk Domain Utilities ────────────────────────────────────────────────────
import { Transaction, TransactionStatus } from '../schemas/transaction';
import { RiskLevel } from '../schemas/common';

/**
 * Computed risk item returned by computeRiskItem.
 * Shared across dashboard, planets, users, and alerts features.
 */
export interface RiskItem<T> {
  entity: T;
  risk: RiskLevel;
  inProgress: number;
  riskScore: number;
  total: number;
}

/**
 * Determines the risk level for a set of transactions based on the in-progress ratio.
 */
export function calcRisk(transactions: Transaction[]): RiskLevel {
  if (!transactions || transactions.length === 0) return 'UNKNOWN';
  const inProgressCount = transactions.filter(
    t => t.status === TransactionStatus.inProgress
  ).length;
  const ratio = inProgressCount / transactions.length;
  if (ratio > 0.3) return 'HIGH';
  if (ratio > 0.1) return 'MEDIUM';
  return 'LOW';
}

/**
 * Canonical risk-level sort order (highest risk first).
 */
export const RISK_ORDER: Record<RiskLevel, number> = {
  HIGH: 0,
  MEDIUM: 1,
  LOW: 2,
  UNKNOWN: 3,
};

/**
 * Sorts an array of items with a `risk` property by risk severity (HIGH → LOW → UNKNOWN).
 */
export function sortByRiskLevel<T extends { risk: RiskLevel }>(items: T[]): T[] {
  return [...items].sort((a, b) => RISK_ORDER[a.risk] - RISK_ORDER[b.risk]);
}

/**
 * Groups items into a Map by a specified key.
 */
export function groupBy<T>(items: T[], keyFn: (item: T) => string | undefined): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = keyFn(item);
    if (!key) continue;
    const list = map.get(key);
    if (list) {
      list.push(item);
    } else {
      map.set(key, [item]);
    }
  }
  return map;
}

/**
 * Computes a full risk item for an entity based on its transactions.
 */
export function computeRiskItem<T>(entity: T, transactions: Transaction[]) {
  const total = transactions.length;
  const inProgressCount = transactions.filter(
    tx => tx.status === TransactionStatus.inProgress
  ).length;
  const riskScore = total > 0 ? Math.round((inProgressCount / total) * 100) : 0;
  return {
    entity,
    risk: calcRisk(transactions),
    inProgress: inProgressCount,
    riskScore,
    total,
  };
}
